import { cn } from "@/lib/utils";
import { BloomProgress } from "../styleguide/ui/BloomProgress";
import { BloomButton } from "../styleguide/ui/BloomButton";
import { Mascot } from "../styleguide/Mascot";
import {
  Calculator,
  BookOpen,
  PencilSimple,
  Flask,
  CaretRight,
  Play,
  Clock,
  Trophy,
  Lightning,
  Star,
  CheckCircle,
  Sparkle,
  ArrowRight,
} from "@phosphor-icons/react";

/* Subject colour map — stays on-brand (orange/yellow/teal/blue). */
export const SUBJECTS = {
  maths: { label: "Maths", icon: Calculator, tint: "bg-blue/12", text: "text-blue-ink", solid: "bg-blue" },
  english: { label: "English", icon: PencilSimple, tint: "bg-primary/12", text: "text-primary-strong", solid: "bg-primary" },
  reading: { label: "Reading", icon: BookOpen, tint: "bg-teal/15", text: "text-teal-ink", solid: "bg-teal" },
  science: { label: "Science", icon: Flask, tint: "bg-secondary/25", text: "text-secondary-ink", solid: "bg-secondary" },
};

/* --------------------------------- Subject Card ---------------------------------- */
export function SubjectCard({ subject = "maths", lessons = 24, done = 9, size = "md" }) {
  const s = SUBJECTS[subject];
  const Icon = s.icon;
  const pad = size === "lg" ? "p-7" : size === "sm" ? "p-5" : "p-6";
  const iconBox = size === "lg" ? "h-16 w-16" : size === "sm" ? "h-11 w-11" : "h-14 w-14";
  const titleCls = size === "lg" ? "text-2xl" : size === "sm" ? "text-lg" : "text-xl";
  return (
    <button
      className={cn(
        "group flex w-full flex-col items-start rounded-3xl border border-border bg-card text-left shadow-soft transition-all duration-300 ease-bounce hover:-translate-y-1.5 hover:border-primary/40 hover:shadow-soft-lg",
        pad,
      )}
    >
      <span className={cn("flex items-center justify-center rounded-2xl", iconBox, s.tint, s.text)}>
        <Icon size={size === "lg" ? 32 : size === "sm" ? 22 : 26} weight="fill" />
      </span>
      <p className={cn("mt-4 font-display font-extrabold", titleCls)}>{s.label}</p>
      <p className="mt-0.5 text-sm font-semibold text-muted-foreground">{done} of {lessons} lessons</p>
      <div className="mt-4 w-full"><BloomProgress value={(done / lessons) * 100} color={subject === "english" ? "primary" : subject === "reading" ? "teal" : subject === "maths" ? "blue" : "secondary"} /></div>
    </button>
  );
}

/* ----------------------------- Continue Learning Card ---------------------------- */
export function ContinueCard() {
  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
      <div className="flex items-center gap-4 bg-gradient-to-r from-primary/15 to-secondary/15 p-5">
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-card text-primary-strong shadow-soft">
          <BookOpen size={28} weight="fill" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-primary-strong">Continue learning</p>
          <p className="truncate font-display text-lg font-extrabold">Reading Quest · Chapter 3</p>
        </div>
      </div>
      <div className="p-5">
        <BloomProgress value={60} color="primary" label="Sounds & words" showValue />
        <BloomButton className="mt-4" fullWidth leftIcon={Play}>Resume</BloomButton>
      </div>
    </div>
  );
}

/* --------------------------------- Homework Card --------------------------------- */
export function HomeworkCard({ due = "Tomorrow", title = "Times tables: 6×", subject = "maths" }) {
  const s = SUBJECTS[subject];
  return (
    <div className="flex items-center gap-4 rounded-3xl border border-border bg-card p-5 shadow-soft">
      <span className={cn("flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl", s.tint, s.text)}>
        <s.icon size={24} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display font-extrabold">{title}</p>
        <p className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <Clock size={15} weight="bold" /> Due {due}
        </p>
      </div>
      <BloomButton size="sm" variant="soft" rightIcon={CaretRight}>Start</BloomButton>
    </div>
  );
}

/* ------------------------------ Daily Challenge Card ----------------------------- */
export function DailyChallengeCard() {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary to-[#FF9E5E] p-6 text-primary-foreground shadow-glow">
      <div aria-hidden className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/15 blur-xl" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
          <Lightning size={14} weight="fill" /> Daily challenge
        </span>
        <p className="mt-4 font-display text-2xl font-black">Beat the clock!</p>
        <p className="mt-1 font-semibold text-primary-foreground/90">Answer 5 questions in 60 seconds.</p>
        <div className="mt-5 flex items-center gap-3">
          <BloomButton variant="secondary" rightIcon={ArrowRight}>Play now</BloomButton>
          <span className="inline-flex items-center gap-1 font-bold"><Star size={18} weight="fill" /> +50 XP</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Achievement Card -------------------------------- */
export function AchievementCard({ unlocked = true }) {
  return (
    <div className={cn("flex flex-col items-center rounded-3xl border border-border bg-card p-6 text-center shadow-soft", !unlocked && "opacity-70")}>
      <span className={cn("flex h-16 w-16 items-center justify-center rounded-full", unlocked ? "bg-secondary/25 text-secondary-ink" : "bg-muted text-muted-foreground")}>
        <Trophy size={32} weight="fill" />
      </span>
      <p className="mt-3 font-display text-lg font-extrabold">Word Wizard</p>
      <p className="text-sm font-semibold text-muted-foreground">{unlocked ? "Unlocked today!" : "Read 10 stories"}</p>
    </div>
  );
}

/* --------------------------------- Lesson Card ----------------------------------- */
export function LessonCard({ title = "Adding to 20", state = "current", subject = "maths" }) {
  const s = SUBJECTS[subject];
  const locked = state === "locked";
  const done = state === "done";
  return (
    <div className={cn("flex items-center gap-4 rounded-3xl border-2 bg-card p-4 shadow-soft transition-all", state === "current" ? "border-primary" : "border-border", locked && "opacity-60")}>
      <span className={cn("flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl", done ? "bg-teal/15 text-teal-ink" : s.tint + " " + s.text)}>
        {done ? <CheckCircle size={26} weight="fill" /> : <s.icon size={24} weight="fill" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display font-extrabold">{title}</p>
        <p className="text-sm font-semibold text-muted-foreground">{done ? "Completed" : locked ? "Locked" : "Up next"}</p>
      </div>
      {state === "current" && <BloomButton size="sm" rightIcon={Play}>Start</BloomButton>}
      {done && <Star size={20} weight="fill" className="text-secondary" />}
    </div>
  );
}

/* ---------------------------------- Topic Card ----------------------------------- */
export function TopicCard({ title = "Fractions", subject = "maths", count = 8 }) {
  const s = SUBJECTS[subject];
  return (
    <button className="group flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40">
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-xl", s.tint, s.text)}>
        <s.icon size={20} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        <p className="text-xs font-semibold text-muted-foreground">{count} topics</p>
      </div>
      <CaretRight size={18} weight="bold" className="text-muted-foreground transition-transform group-hover:translate-x-1" />
    </button>
  );
}

/* ------------------------------- Empty State Card -------------------------------- */
export function EmptyStateCard({
  title = "No homework yet!",
  message = "When your teacher sets homework, it'll appear here.",
  actionLabel = "Explore lessons",
}) {
  return (
    <div className="flex flex-col items-center rounded-3xl border-2 border-dashed border-border bg-card p-8 text-center">
      <Mascot size={96} mood="happy" />
      <p className="mt-3 font-display text-xl font-extrabold">{title}</p>
      <p className="mt-1 max-w-xs font-medium text-muted-foreground">{message}</p>
      <BloomButton className="mt-5" variant="soft" rightIcon={Sparkle}>{actionLabel}</BloomButton>
    </div>
  );
}
